/**
 * meta-attribution.ts — Meta (Facebook) app events + App Tracking Transparency
 *
 * Lets Meta Ads attribute iOS installs. The Meta SDK itself (FBSDKCoreKit,
 * pulled in by @capacitor-community/facebook-login) is started natively in
 * AppDelegate at launch, with automatic app events on — so installs and app
 * opens are logged without any JS. This file only does three small extras:
 *
 *   1. syncTrackingStatus() — on every launch, tell the Meta SDK whether the
 *      person has allowed tracking (ATT), so it only uses the advertising ID
 *      when they said yes.
 *   2. maybeRequestTracking() — show Apple's "Allow tracking?" prompt once,
 *      after sign-in and onboarding (never on the first screen).
 *   3. logMetaRegistration() — log Meta's standard "Complete registration"
 *      event once when a new account is created.
 *
 * Safety:
 *   - iOS native only. Every function is a no-op on the website and Android.
 *   - The web bundle is served live to every installed iOS build, including
 *     1.3.3 and older, which don't have these native plugins. So we check
 *     Capacitor.isPluginAvailable() before every call — on older builds all
 *     of this silently does nothing.
 *   - Nothing here ever throws; all failures are swallowed with a warning.
 */

import { Capacitor } from "@capacitor/core";
import { FacebookLogin } from "@capacitor-community/facebook-login";
import {
  AppTrackingTransparency,
  type AppTrackingStatus,
} from "capacitor-plugin-app-tracking-transparency";

const isIos = Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

function hasMeta(): boolean {
  return isIos && Capacitor.isPluginAvailable("FacebookLogin");
}
function hasAtt(): boolean {
  return isIos && Capacitor.isPluginAvailable("AppTrackingTransparency");
}

function get(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function set(key: string, val: string) {
  try { localStorage.setItem(key, val); } catch { /* storage off */ }
}

async function applyStatusToMeta(status: AppTrackingStatus) {
  if (!hasMeta()) return;
  try {
    // Meta SDK 17+ also reads the ATT status itself; setting it keeps older
    // SDK behaviour correct too and costs nothing.
    await FacebookLogin.setAdvertiserTrackingEnabled({ enabled: status === "authorized" });
  } catch (err) {
    console.warn("[meta] setAdvertiserTrackingEnabled failed:", err);
  }
}

/** Launch-time sync of the current ATT choice into the Meta SDK. Never prompts. */
export async function syncTrackingStatus(): Promise<void> {
  if (!hasAtt()) return;
  try {
    const { status } = await AppTrackingTransparency.getStatus();
    await applyStatusToMeta(status);
  } catch (err) {
    console.warn("[meta] ATT getStatus failed:", err);
  }
}

let _asking = false;
const ASK_DELAY_MS = 1500; // let the screen settle (and any push prompt clear) first

/**
 * Ask Apple's tracking question — only if it hasn't been answered yet on this
 * device. iOS itself only ever shows it once; if it couldn't show (e.g. another
 * system alert was up) the status stays "notDetermined" and we try again on a
 * later launch. Call once the person is signed in and past onboarding.
 */
export function maybeRequestTracking(): Promise<void> {
  if (!hasAtt() || _asking) return Promise.resolve();
  _asking = true;
  // Resolves once the tracking question is answered (or skipped), so the next
  // system prompt can wait its turn instead of colliding with this one.
  return new Promise((resolve) => {
    setTimeout(async () => {
      try {
        const { status } = await AppTrackingTransparency.getStatus();
        if (status !== "notDetermined") {
          await applyStatusToMeta(status);
          return;
        }
        const result = await AppTrackingTransparency.requestPermission();
        await applyStatusToMeta(result.status);
      } catch (err) {
        console.warn("[meta] ATT request failed:", err);
        _asking = false; // allow a retry later this session
      } finally {
        resolve();
      }
    }, ASK_DELAY_MS);
  });
}

/**
 * Log Meta's standard "Complete registration" app event, once per account on
 * this device. Safe to call more than once.
 */
export function logMetaRegistration(userId: string | number): void {
  if (!hasMeta()) return;
  const key = `spliiit_meta_reg_${userId}`;
  if (get(key)) return;
  set(key, "1");
  // "fb_mobile_complete_registration" = AppEvents.Name.completedRegistration
  FacebookLogin.logEvent({ eventName: "fb_mobile_complete_registration" }).catch((err) => {
    console.warn("[meta] logEvent failed:", err);
  });
}
